import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./icons";
import { LoadingBlock, cx } from "./ui";
import type { RegionCatalogue } from "../lib/types";

/**
 * Where this workload will live: Europe, America, Asia or Australia. The
 * customer picks the continent; placement picks the nearest server inside it.
 *
 * One segmented control. Auto leads and is the default, and it says what it
 * would do — the API reports the nearest region to where the account was
 * created — so the default is a statement rather than a mystery. Each region
 * carries a status dot: filled when an eligible server sits inside it right
 * now, hollow and dimmed with "Unavailable" when none does. A region with
 * no server is shown, not hidden: the list is the platform's footprint.
 *
 * The choice is the workload's, not the organization's. An organization
 * holds one placement per region it uses, so a region it already runs
 * something in is marked "In use" and stays choosable, and the rest are
 * open too. The one lock is a tenant pinned to a dedicated server: every
 * workload lands there, so the bar shows that region selected with a lock
 * and nothing can be chosen.
 */
export function RegionPicker({
  catalogue,
  loading,
  value,
  onChange,
}: {
  catalogue: RegionCatalogue | undefined;
  loading: boolean;
  /** A region code, or null for automatic placement. */
  value: string | null;
  onChange: (region: string | null) => void;
}) {
  const { t } = useTranslation("create");
  if (loading) return <LoadingBlock label={t("regions.loading")} />;

  /*
   * The catalogue failing to load must not block the create. Auto is what the
   * API does when no region is sent, so the form still works exactly as it did
   * before regions were offered — it just cannot offer them.
   */
  if (!catalogue) {
    return <p className="text-sm text-fg-muted">{t("regions.placedAutomatically")}</p>;
  }

  const { data, placement } = catalogue;
  const labelOf = (code: string | null) => data.find((r) => r.code === code)?.label ?? null;

  if (placement.pinned) {
    return (
      <Control role="radiogroup" label={t("regions.label")}>
        {data.map((region) => (
          <Segment
            key={region.code}
            selected={region.code === placement.pinned}
            available={region.available}
            tone="status"
            label={region.label}
            detail={region.available ? null : t("regions.unavailableShort")}
            locked
          />
        ))}
      </Control>
    );
  }

  return (
    <Control role="radiogroup" label={t("regions.label")}>
      <Segment
        selected={value === null}
        available
        tone="accent"
        label={t("regions.auto")}
        detail={labelOf(placement.suggested)}
        onSelect={() => onChange(null)}
      />
      {data.map((region) => (
        <Segment
          key={region.code}
          selected={value === region.code}
          available={region.available}
          placed={region.placed}
          tone="status"
          label={region.label}
          detail={
            region.available
              ? region.placed
                ? t("regions.inUse")
                : null
              : t("regions.unavailable")
          }
          onSelect={() => onChange(region.code)}
        />
      ))}
    </Control>
  );
}

const SEGMENT =
  "flex min-w-[7.5rem] flex-1 items-center gap-2.5 rounded-md px-3 py-2 text-left " +
  "transition-all duration-200 ease-smooth";

/** The bar the segments sit in: one sunken track, segments float on it. */
function Control({
  role,
  label,
  children,
}: {
  role?: "radiogroup";
  label?: string;
  children: ReactNode;
}) {
  return (
    <div
      role={role}
      aria-label={label}
      className="flex flex-wrap gap-1 rounded-lg border border-line bg-sunken p-1"
    >
      {children}
    </div>
  );
}

/**
 * One segment. Unavailable ones stay in the bar — they are information —
 * but are not focusable and say so in their own text: `aria-disabled` rather
 * than `disabled`, so a screen reader still reads the region's name and why.
 * A placed one is marked and remains a choice. A locked bar disables every
 * segment; the selected one carries a lock instead of a check, and says on
 * hover why it cannot change.
 */
function Segment({
  selected,
  available,
  placed = false,
  tone,
  label,
  detail,
  locked = false,
  onSelect,
}: {
  selected: boolean;
  available: boolean;
  /** The organization already runs something here. */
  placed?: boolean;
  /** Auto's dot is the accent; a region's dot reports its capacity. */
  tone: "accent" | "status";
  label: string;
  detail: string | null;
  /** The tenant is on a dedicated server; nothing in the bar can be chosen. */
  locked?: boolean;
  onSelect?: () => void;
}) {
  const { t } = useTranslation("create");
  const choosable = available && !locked;
  const title = locked
    ? t("regions.dedicated")
    : available
      ? undefined
      : t("regions.unavailableTitle");
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={!choosable}
      tabIndex={choosable ? 0 : -1}
      title={title}
      onClick={choosable ? onSelect : undefined}
      {...(locked && selected ? { "data-testid": "region-locked" } : {})}
      {...(placed ? { "data-testid": "region-placed" } : {})}
      className={cx(
        SEGMENT,
        selected
          ? "bg-raised text-fg shadow-sm ring-1 ring-line-strong"
          : choosable
            ? "text-fg-muted hover:bg-raised/60 hover:text-fg"
            : "cursor-not-allowed text-fg-subtle",
      )}
    >
      {locked && selected ? (
        <Icon name="lock" className="size-3.5 shrink-0 text-fg-subtle" />
      ) : (
        <span
          aria-hidden="true"
          className={cx(
            "size-1.5 shrink-0 rounded-full",
            tone === "accent" ? "bg-accent" : available ? "bg-mint" : "border border-line-strong",
          )}
        />
      )}
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="text-[13px] font-medium">{label}</span>
        {detail && <span className="text-[11px] text-fg-subtle">{detail}</span>}
      </span>
      {selected && !locked && (
        <Icon name="check" className="ml-auto size-3.5 shrink-0 text-accent" />
      )}
    </button>
  );
}
